import { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchData, fetchResponse, fetchGeeklists, fetchPriceHistory } from '../services';
import { parseXml } from '../helpers';
import { extractExtras, findPriceAndType, findRemove, findSold, findAuctionEndDate, useBggUsers } from '../helpers';
import { getDistance } from '../helpers/geocode-distance';
import { GeeklistItem } from '../objects';
import { GlobalStateContext } from '../GlobalState';
import { 
    BGG_BASE_URL, 
    LOADING_MESSAGES, 
    ERROR_MESSAGES, 
    SUCCESS_MESSAGES, 
    TOAST_OPTIONS
} from '../constants';
import { toast } from 'react-toastify';

/**
 * Custom hook for managing geeklist state and operations
 * Extracts all business logic from the BggList component
 */
const useGeeklist = (gridRef) => {
    const { geeklistMeta, setGeeklistMeta, geeklistItems, setGeeklistItems, usernameValue, setUsernameValue, controlState, setControlState, isGridExpanded, setIsGridExpanded } = useContext(GlobalStateContext);
    const [searchParams] = useSearchParams();

    // Local state
    const [wishlistData, setWishlistData] = useState([]);
    const [hiddenRows, setHiddenRows] = useState(new Set());
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);
    const [hasSelectedRows, setHasSelectedRows] = useState(false);
    const [listIdValue, setlistIdValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [progressValues, setProgressValues] = useState({message: LOADING_MESSAGES.DEFAULT, progress: 0, type: 'progress'});
    const [rowCount, setRowCount] = useState(0);
    const [geeklists, setGeeklists] = useState([]);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [itemSearchTerm, setItemSearchTerm] = useState('');

    const { findBggUser } = useBggUsers();
    const hasLoadedRef = useRef({});
    const base_url = "/api/listitems";

    // Fetch geeklists on mount
    useEffect(() => {
        const loadGeeklists = async () => {
            const result = await fetchGeeklists();
            if (result && result.geeklists) {
                setGeeklists(result.geeklists);
            }
        };
        loadGeeklists();
    }, []);

    // Handle URL parameters
    useEffect(() => {
        const geeklistId = searchParams.get('geeklistId');
        if (geeklistId && geeklists.length > 0) {
            setlistIdValue(geeklistId);
            setControlState(false);
        }
        
        // Initialize advanced view from URL parameter
        const showAdvancedParam = searchParams.get('showAdvanced');
        if (showAdvancedParam !== null) {
            setShowAdvanced(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, geeklists.length]);

    // Update URL when advanced view state changes
    useEffect(() => {
        const newParams = new URLSearchParams(searchParams);
        if (showAdvanced) {
            newParams.set('showAdvanced', 'true');
        } else {
            newParams.delete('showAdvanced');
        }
        window.history.replaceState(null, '', window.location.pathname + '?' + newParams.toString());
    }, [showAdvanced, searchParams]);

    // Load geeklist data when listIdValue changes
    useEffect(() => {
        if (listIdValue && !isLoading && !hasLoadedRef.current[listIdValue]) {
            hasLoadedRef.current[listIdValue] = true;
            setIsLoading(true);
            setProgressValues({message: LOADING_MESSAGES.GEEKLIST_ITEMS, progress: 0, type: "progress"});
            setTimeout(() => fetchGeeklistData(listIdValue), 0);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listIdValue, isLoading]);

    // Handle advanced columns visibility
    useEffect(() => {
        if (gridRef.current && gridRef.current.api) {
            const columnsToToggle = ['dealScore', 'matches', 'extrasIds', 'stats.average', 'postdate', 'editdate', 'endDate', 'location', 'expansion'];
            columnsToToggle.forEach(col => {
                gridRef.current.api.setColumnVisible(col, showAdvanced);
            });
        }
    }, [showAdvanced, gridRef]);

    // Fetch and process geeklist data
    const fetchGeeklistData = useCallback(async (listId) => {
        if (!listId) return;

        let loadingMessage = LOADING_MESSAGES.ITEMS;
        setProgressValues({message: loadingMessage, progress: 0, type: "progress"});
        setIsLoading(true);

        try {
            let total = 0;
            let lastpage = 10;
            const newData = [];

            const geeklistMetaUrl = `/api/geeklists/${listId}`;
            const firstPageUrl = `${base_url}?listid=${listId}&page=1`;
            const geeklistMetaResult = await fetchResponse(geeklistMetaUrl);
            const firstPageResult = await fetchData(firstPageUrl);
            setGeeklistMeta(geeklistMetaResult.data);

            if (!firstPageResult || !geeklistMetaResult) {
                toast.error(`Error fetching data for list ID: ${listId}. Please try again.`);
                setIsLoading(false);
                return;
            }

            if (!firstPageResult.data || !Array.isArray(firstPageResult.data)) {
                setIsLoading(false);
                return;
            }

            newData.push(...firstPageResult.data);

            if (firstPageResult.pagination) {
                total = firstPageResult.pagination.total;
                lastpage = Math.ceil(total / 25);
            }

            setProgressValues({ message: loadingMessage, progress: (1 / lastpage) * 100, type: "progress" });

            if (lastpage > 1) {
                const pagePromises = [];
                for (let page = 2; page <= lastpage; page++) {
                    pagePromises.push(fetchData(`${base_url}?listid=${listId}&page=${page}`));
                }

                let completedPages = 1;
                const pagePromisesWithProgress = pagePromises.map((promise, index) => {
                    return promise.then(result => {
                        if (result && result.data) {
                            newData.push(...result.data);
                        }
                        completedPages++;
                        setProgressValues({message: loadingMessage, progress: (completedPages / lastpage) * 100, type: "progress"});
                        return result;
                    }).catch(error => {
                        completedPages++;
                        setProgressValues({message: loadingMessage, progress: (completedPages / lastpage) * 100, type: "progress"});
                        return null;
                    });
                });

                await Promise.all(pagePromisesWithProgress);
            }

            if (newData.length === 0) {
                setIsLoading(false);
                return;
            }

            // Filter removed listings
            const filteredData = newData.filter((item) => !findRemove(item.body));

            // Fetch user data
            loadingMessage = LOADING_MESSAGES.USERNAMES;
            setProgressValues({message: loadingMessage, progress: 0, type: "progress"});

            const uniqueAuthorIds = [...new Set(filteredData.map(item => item.author))];
            const userMap = new Map();

            for (let i = 0; i < uniqueAuthorIds.length; i++) {
                const user = await findBggUser(uniqueAuthorIds[i]);
                if (user) {
                    userMap.set(user.id, user);
                }
                setProgressValues({message: loadingMessage, progress: (i / uniqueAuthorIds.length) * 100, type: "progress"});
            }

            // Process data into GeeklistItem objects
            const filteredDataWithUsername = filteredData.map(item => {
                const user = userMap.get(item.author);
                return user ? { ...item, username: user.username, city: user.city, state: user.state } : item;
            });

            const processedData = filteredDataWithUsername.map(item => {
                const user = userMap.get(item.author);
                const geeklistItem = GeeklistItem.fromApiResponse(item, user, {
                    findPriceAndType,
                    extractExtras,
                    findSold,
                    findAuctionEndDate,
                    BGG_BASE_URL
                });
                geeklistItem.matches = filteredDataWithUsername.filter(otherItem => otherItem.id !== item.id && otherItem.item.id === item.item.id).length;
                return geeklistItem;
            });

            setGeeklistItems(processedData);
            toast.success(SUCCESS_MESSAGES.GEEKLIST_LOADED, TOAST_OPTIONS.AUTO_CLOSE_TRUE);
        } catch (error) {
            console.error(ERROR_MESSAGES.FETCH_ITEM_DATA, error);
            toast.error(ERROR_MESSAGES.FETCH_ITEM_DATA, TOAST_OPTIONS.AUTO_CLOSE_FALSE);
        } finally {
            setIsLoading(false);
        }
    }, [findBggUser, setGeeklistItems, setGeeklistMeta]);

    // Fetch wishlist data
    const fetchWishlistData = useCallback(async () => {
        setProgressValues({message: "Wishlist attempt 1", progress: 0, type: "spinner"});
        setIsLoading(true);

        let iterations = 1;
        let result = "";

        while (true) {
            const encodedURI = encodeURIComponent(`/collection?username=${usernameValue}&wishlist=1`);
            result = await fetchResponse(`/.netlify/functions/fetch-bgg-xml?path=${encodedURI}`);
            
            if (result && result.status !== 200) {
                await new Promise(resolve => setTimeout(resolve, 5000));
                if (iterations > 4) break;
                iterations++;
                setProgressValues({message: `Wishlist attempt ${iterations}`, progress: (iterations/5)*100});
                continue;
            }
            break;
        }

        if (!result || result.status !== 200) {
            toast.error(ERROR_MESSAGES.FETCH_WISHLIST, TOAST_OPTIONS.AUTO_CLOSE_FALSE);
            setIsLoading(false);
            return;
        }

        const xmlDoc = await parseXml(result.data);
        const wishlistItems = xmlDoc.getElementsByTagName('item');
        const wishlistIds = Array.from(wishlistItems).map(item => item.getAttribute('objectid'));

        setWishlistData(wishlistIds);
        setIsLoading(false);
    }, [usernameValue]);

    // Update distances based on zip code
    const updateDistances = useCallback(async (zip) => {
        if (!zip || zip.trim() === '' || geeklistItems.length === 0) return;

        setProgressValues({message: LOADING_MESSAGES.DISTANCES, progress: 0, type: "spinner"});
        setIsLoading(true);

        const updatedItems = [...geeklistItems];

        for (let i = 0; i < updatedItems.length; i++) {
            const item = updatedItems[i];
            if (!item.city || !item.state) {
                updatedItems[i] = { ...item, location: { ...item.location, distance: Infinity } };
            } else {
                const distance = await getDistance(zip.trim(), `${item.city}, ${item.state}`);
                updatedItems[i] = { ...item, location: { ...item.location, distance } };
            }
        }

        setGeeklistItems(updatedItems);
        gridRef.current?.api?.refreshCells({ columns: ['location'], force: true });
        setIsLoading(false);
    }, [geeklistItems, setGeeklistItems, gridRef]);

    // Calculate deal score
    const calculateDealScore = useCallback((listPrice, averagePrice) => {
        if (!averagePrice || averagePrice === 0) return "N/A";
        if (listPrice === "?") return "?";
        return (100 - (listPrice / averagePrice) * 100);
    }, []);

    // Update deal score for a single listing
    const updateDealScore = useCallback(async (listing) => {
        try {
            const hasExtras = listing.extrasIds.length > 0;
            const gameId = listing.item.id;
            const mainPriceHistory = await fetchPriceHistory(gameId);
            let averagePrice = mainPriceHistory.averagePrice;
            let allPriced = true;

            if (hasExtras) {
                let totalPrice = +averagePrice;
                for (const extraId of listing.extrasIds) {
                    try {
                        const extraPriceHistory = await fetchPriceHistory(extraId);
                        if (extraPriceHistory.averagePrice > 0) {
                            totalPrice += +extraPriceHistory.averagePrice;
                        } else {
                            allPriced = false;
                        }
                    } catch {
                        allPriced = false;
                    }
                }
                averagePrice = totalPrice % 1 === 0 ? totalPrice : parseFloat(totalPrice.toFixed(2));
            }

            const updatedItems = [...geeklistItems];
            const targetItems = hasExtras 
                ? updatedItems.filter(item => item.id === listing.id)
                : updatedItems.filter(item => item.item.id === gameId && item.extrasIds.length === 0);

            if (targetItems.length === 0) return null;

            const dealScore = {
                    averagePrice,
                    score: calculateDealScore(listing.price, averagePrice),
                    includesExtras: hasExtras,
                    allPriced
            };

            for (const item of targetItems) {
                item.dealScore = {
                        averagePrice,
                        score: calculateDealScore(item.price, averagePrice),
                        includesExtras: hasExtras,
                        allPriced
                };
            }

            // Use AG Grid's transaction API to update only the changed rows
            // This preserves scroll position and is more performant
            if (gridRef.current?.api) {
                gridRef.current.api.applyTransaction({ update: updatedItems });
            }

            return dealScore;
        } catch (error) {
            console.error("Error in updateDealScore:", error);
            return null;
        }
    }, [geeklistItems, calculateDealScore, gridRef]);

    // Bulk update deal scores
    const updateBulkDealScores = useCallback(async () => {
        if (!gridRef.current?.api) return;

        const listingsToUpdate = new Set();
        gridRef.current.api.forEachNodeAfterFilter((node) => {
            if (node.data && (!node.data.dealScore || node.data.dealScore.score === null || node.data.dealScore.score === "")) {
                listingsToUpdate.add(node.data);
            }
        });

        if (listingsToUpdate.size === 0) return;

        setProgressValues({message: LOADING_MESSAGES.PRICES, progress: 0, type: "progress"});
        setIsLoading(true);

        const listingsArray = Array.from(listingsToUpdate);
        let completedCount = 0;

        const promises = listingsArray.map(listing => {
            return updateDealScore(listing).then(() => {
                completedCount++;
                setProgressValues({message: LOADING_MESSAGES.PRICES, progress: (completedCount / listingsArray.length) * 100, type: "progress"});
            }).catch(() => {
                completedCount++;
                setProgressValues({message: LOADING_MESSAGES.PRICES, progress: (completedCount / listingsArray.length) * 100, type: "progress"});
            });
        });

        await Promise.allSettled(promises);
        setIsLoading(false);
    }, [updateDealScore, gridRef]);

    // External filter functions for AG Grid
    const isExternalFilterPresent = useCallback(() => {
        return (wishlistData.length > 0 || hiddenRows.size > 0 || itemSearchTerm.trim() !== '');
    }, [wishlistData, hiddenRows, itemSearchTerm]);

    const doesExternalFilterPass = useCallback((node) => {
        if (hiddenRows.has(node.data.id)) return false;
        if (itemSearchTerm.trim()) {
            const name = node.data.item.name.toLowerCase();
            const body = node.data.body.toLowerCase();
            const seller = node.data.username.toLowerCase();
            const term = itemSearchTerm.toLowerCase();
            if (!name.includes(term) && !body.includes(term) && !seller.includes(term)) return false;
        }
        if (wishlistData.length > 0) {
            return wishlistData.includes(node.data.item.id);
        }
        return true;
    }, [hiddenRows, itemSearchTerm, wishlistData]);

    // Grid handlers
    const handleSoldFilterClick = useCallback(() => {
        if (!gridRef.current) return;
        const currentFilter = gridRef.current.api.getFilterModel().sold;
        gridRef.current.api.setFilterModel({
            ...gridRef.current.api.getFilterModel(),
            sold: currentFilter === undefined ? { filterType: 'text', type: 'equals', filter: 'false' } : undefined
        });
        gridRef.current.api.onFilterChanged();
    }, [gridRef]);

    const updateRowCount = useCallback(() => {
        if (!gridRef.current?.api) return;
        setRowCount(gridRef.current.api.getDisplayedRowCount());
        let hasSelected = false;
        gridRef.current.api.forEachNode(node => {
            if (node.data?.selected === 'True') hasSelected = true;
        });
        setHasSelectedRows(hasSelected);
    }, [gridRef]);

    const clearAllSelections = useCallback(() => {
        if (!gridRef.current?.api) return;
        gridRef.current.api.forEachNode(node => {
            if (node.data?.selected === 'True') {
                node.setDataValue('selected', 'False');
            }
        });
        setHasSelectedRows(false);
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('selected');
        window.history.replaceState(null, '', window.location.pathname + '?' + newParams.toString());
        gridRef.current.api.setFilterModel({...gridRef.current.api.getFilterModel(), selected: undefined});
        setShowSelectedOnly(false);
    }, [gridRef, searchParams]);

    const clearAllFilters = useCallback(() => {
        if (!gridRef.current?.api) return;
        gridRef.current.api.setFilterModel({});
        setShowSelectedOnly(false);
    }, [gridRef]);

    const toggleSelectedFilter = useCallback(() => {
        if (!gridRef.current?.api) return;
        const currentFilter = gridRef.current.api.getFilterModel().selected;
        const newFilter = currentFilter === undefined ? { filterType: 'text', type: 'equals', filter: 'True' } : undefined;
        gridRef.current.api.setFilterModel({...gridRef.current.api.getFilterModel(), selected: newFilter});
        gridRef.current.api.onFilterChanged();
        setShowSelectedOnly(newFilter !== undefined);
    }, [gridRef]);

    const selectAll = useCallback(() => {
        if (!gridRef.current?.api) return;
        gridRef.current.api.forEachNode(node => {
            if (node.data?.selected !== 'True') {
                node.setDataValue('selected', 'True');
            }
        });
        setHasSelectedRows(true);
    }, [gridRef]);

    const filterToSeller = useCallback((seller) => () => {
        if (!gridRef.current?.api) return;
        const currentFilter = gridRef.current.api.getFilterModel().username;
        const newFilter = (!seller || currentFilter !== undefined) ? undefined : { filterType: 'text', type: 'equals', filter: seller };
        gridRef.current.api.setFilterModel({...gridRef.current.api.getFilterModel(), username: newFilter});
        gridRef.current.api.onFilterChanged();
    }, [gridRef]);

    const updateUrlWithSelectedItems = useCallback(() => {
        if (!gridRef.current?.api) return;
        const selectedItemIds = [];
        gridRef.current.api.forEachNode(node => {
            if (node.data?.selected === 'True') selectedItemIds.push(node.data.id);
        });
        setHasSelectedRows(selectedItemIds.length > 0);
        const newParams = new URLSearchParams(searchParams);
        if (selectedItemIds.length > 0) {
            newParams.set('selected', selectedItemIds.join(','));
        } else {
            newParams.delete('selected');
        }
        window.history.replaceState(null, '', window.location.pathname + '?' + newParams.toString());
    }, [gridRef, searchParams]);

    const clearWishlist = useCallback(() => setWishlistData([]), []);

    const setListId = useCallback((id) => {
        hasLoadedRef.current[id] = false;
        setlistIdValue(id);
    }, []);

    return {
        // State
        geeklistMeta,
        geeklistItems,
        geeklists,
        isLoading,
        progressValues,
        rowCount,
        showSelectedOnly,
        hasSelectedRows,
        showAdvanced,
        itemSearchTerm,
        wishlistData,
        hiddenRows,
        controlState,
        isGridExpanded,
        usernameValue,
        listIdValue,
        
        // Setters
        setShowAdvanced,
        setItemSearchTerm,
        setControlState,
        setIsGridExpanded,
        setUsernameValue,
        setHiddenRows,
        
        // Data operations
        setListId,
        fetchWishlistData,
        clearWishlist,
        updateDistances,
        updateDealScore,
        updateBulkDealScores,
        
        // Grid operations
        isExternalFilterPresent,
        doesExternalFilterPass,
        handleSoldFilterClick,
        updateRowCount,
        clearAllSelections,
        clearAllFilters,
        toggleSelectedFilter,
        selectAll,
        filterToSeller,
        updateUrlWithSelectedItems,
    };
};

export default useGeeklist;